import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '../../../../components/general';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { File } from '../../../../types';
import { getSampleSelected } from '../../../../utils';

import ThumbnailsContainer from '../../components/thumbnailsContainer';
import { UploadingClips } from '../../components/types';

interface Props {
  setUploadedClips: React.Dispatch<React.SetStateAction<File[]>>;
  setUploadingClips: React.Dispatch<React.SetStateAction<UploadingClips>>;
  setUploadingCount: React.Dispatch<React.SetStateAction<number>>;
}

const SampleComponent: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { setUploadedClips, setUploadingClips, setUploadingCount } = props;

  const { isSample, sampleProjectData, updateSampleProjectData } =
    useSampleProjectData(eventId);
  const sampleClips = useMemo(
    () => sampleProjectData.clips ?? [],
    [sampleProjectData.clips],
  );

  const toggleClipSelection = (id: string, isAdd?: boolean) => {
    const clips = [...sampleClips];
    const foundClip = clips.find((clip) => clip.elementId === id);

    foundClip.isSelected = isAdd ?? !foundClip.isSelected;

    updateSampleProjectData({ ...sampleProjectData, clips });
  };

  const handleSelectAll = () => {
    sampleClips.forEach((clip: any) =>
      toggleClipSelection(clip.elementId, true),
    );
  };

  const imitateUploading = () => {
    setUploadedClips([]);
    setUploadingCount(sampleClips.length);

    // add all clips to uploading list
    const uploadingClipsTemp: UploadingClips = {};
    sampleClips.forEach((clip: any) => {
      uploadingClipsTemp[clip.elementId] = {
        id: clip.elementId,
        name: clip.name,
        progress: 0,
      };
    });
    setUploadingClips(uploadingClipsTemp);

    // imitate uploading clips
    const interval = setInterval(() => {
      setUploadingClips((prevUploadingClips) => {
        const nextClips = { ...prevUploadingClips };

        // get progress of first clip – as all clips progress equally
        let prc = nextClips[Object.keys(nextClips)[0]].progress;

        if (prc < 100) {
          prc++;

          Object.keys(nextClips).map(
            (id) => (nextClips[id] = { ...nextClips[id], progress: prc }),
          );

          return nextClips;
        }

        // when upload 'complete' – set clips array
        setUploadedClips(sampleClips);

        sampleProjectData.isClipsUploaded = true;
        updateSampleProjectData(sampleProjectData);

        clearInterval(interval);

        return {};
      });
    }, 15 * sampleClips.length);

    return () => clearInterval(interval);
  };

  const isBtnDisabled = useMemo(() => {
    return getSampleSelected(sampleClips).length < sampleClips.length;
  }, [sampleClips]);

  if (!isSample) return null;

  return (
    <React.Fragment>
      <Button onClick={handleSelectAll} variant="link" className="self-center">
        Select All
      </Button>

      <ThumbnailsContainer
        videos={sampleClips}
        handleClick={(video) => toggleClipSelection(video.elementId)}
        isBig
        isSample
      />

      <Button
        onClick={imitateUploading}
        className="w-full sm:w-1/2 self-center"
        disabled={isBtnDisabled}
        allowPointerEvents
        popover={{
          header: 'Cannot upload',
          body: 'Select all clips to upload',
          hide: !isBtnDisabled,
        }}
      >
        Upload
      </Button>
    </React.Fragment>
  );
};

export default SampleComponent;
